/*============================================================
   REACT DEPENDENCIES
============================================================*/
import { useState, useEffect } from "react";

/*============================================================
   REDUX DEPENDENCIES
============================================================*/
import { useDispatch, useSelector } from "react-redux";

import { FormControl } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import TextareaAutosize from "@mui/material/TextareaAutosize";

import GridSectionHeader from "../../../widgets/GridSectionHeader";
import GridCheckbox from "../../../widgets/GridCheckbox";

import { valudateTextfield2 } from "../../../../utils/fieldValidator.js";
import { MAX_LENGTH_TEXTAREA } from "../../../../utils/constants.js";

//DATA SLICE - REDUCER
import { updateCKACKR } from "../../../../states/slcCkaCkr.js";

var isWorkExperiencedError = "";
var experienceTypeError = "";
var workOrganisationNameError = "";

//TRUE - WHEN COMPONENT IS VALID
var validCKAWorkExperience = false;

export function getCKAWorkExperienceValidity() {
  return validCKAWorkExperience;
}

export default function SectionCKAWorkExperience({ setEnableNext }) {
  const ckackr = useSelector((state) => state.ckackr);

  const [isWorkExperienced, setIsWorkExperienced] = useState(ckackr.isWorkExperienced);
  const [experienceType, setExperienceType] = useState(Array(3).fill(""));
  const [workOrganisationName, setWorkOrganisationName] = useState(ckackr.workOrganisationName);

  const dispatch = useDispatch();

  useEffect(() => {
    const data = {
      isWorkExperienced: isWorkExperienced,
      experienceType: experienceType,
      workOrganisationName: workOrganisationName,
    };
    updateRedux("experience", data, updateCKACKR);
  }, [isWorkExperienced, experienceType, workOrganisationName]);

  function updateRedux(fieldName, fieldValue, reducer) {
    const action = {
      type: fieldName,
      payload: fieldValue,
    };
    dispatch(reducer(action));
  }

  function handleChange(e, id) {
    let input_data = {
      event: e,
      fieldName: "",
      fieldVariable: "",
      isMandatory: false,
    };

    input_data.e = e;

    let type = "";
    let name = "";

    id = e.target.id.toLowerCase();

    //RADIOBOX
    if (id === "") {
      type = e.target.type;
      name = e.target.name;
      validateRadiobox(e, type, name);
    }

    console.log(id);
    //CHECKBOX
    validateCheckbox(e, id);

    //TEXTFIELDS
    validateTextfield(id, input_data);

    calculateValidity();
  }

  function validateRadiobox(e, type, name) {
    if (type === "radio" && name === "isWorkExperienced") {
      setIsWorkExperienced(e.target.value);
      isWorkExperiencedError = "";
    }
  }

  function validateCheckbox(e, id) {
    const newArr = [...experienceType];
    const selectedWorkExperience = e.target.value.toUpperCase();
    let index = 0;

    if (id !== "") {
      switch (selectedWorkExperience) {
        case "FINANCE_GENERIC":
          index = 0;
          break;

        case "FINANCE_TRAINER":
          index = 1;
          break;

        case "FINANCE_LEGAL":
          index = 2;
          break;

        default:
          index = -1;
      }

      newArr[index] = newArr[index] === "" ? selectedWorkExperience : "";
      setExperienceType(newArr);

      //Check & indicate an education is already selected
      let isSelected = false;
      for (const experience of newArr) {
        if (experience.length !== 0) {
          isSelected = true;
          break;
        }
      }
      if (isSelected) experienceTypeError = "";
      else experienceTypeError = "Experience type must be selected";
    }
  }

  function validateTextfield(id, input_data) {
    if (id === "workorganisationname") {
      input_data.fieldName = "Work organisation description";
      input_data.fieldVariable = workOrganisationName;
      input_data.fieldLength = MAX_LENGTH_TEXTAREA;
      input_data.isMandatory = true;
      const result = valudateTextfield2(input_data);

      if (result.errorMessage === "") {
        setWorkOrganisationName(result.value);
        workOrganisationNameError = "";
        //updateRedux("networthImpactDesc", result.value, updateSummary);
      } else workOrganisationNameError = "Work organisation description is required";
    }
  }

  function calculateValidity() {
    validCKAWorkExperience = false;

    if (
      isWorkExperiencedError.length !== 0 ||
      experienceTypeError.length !== 0 ||
      workOrganisationNameError.length !== 0
    ) {
      validCKAWorkExperience = false;
      setEnableNext(false);
      return;
    }

    validCKAWorkExperience = true;
    setEnableNext(true);
  }

  return (
    <>
      <Grid container spacing={2}>
        <GridSectionHeader
          sectionName="Work Experience"
          sx={{
            p: 1,
            pl: 1.8,
            mt: 0,
            mb: 1,
            fontSize: 18,
            fontWeight: "bold",
            color: "#FFFFFF", //WHITE
            // color: "#000000", //BLACK
            // backgroundColor: "#d9d9d9",
            backgroundColor: "#10B981",
          }}
        />

        <Typography variant="body1">
          4. Do you have a minimum of 3 contnuous years of experience in the preceding 10 years involving the follwing
          fields?
        </Typography>
      </Grid>

      <RadioGroup row name="isWorkExperienced" defaultValue="" value={isWorkExperienced} onChange={handleChange}>
        <FormControl>
          <Stack direction="row">
            <FormControlLabel id="isWorkExperiencedYes" control={<Radio />} label="Yes" value="Yes" />

            <FormControlLabel id="isWorkExperiencedNo" control={<Radio />} label="No" value="No" />
          </Stack>
        </FormControl>
      </RadioGroup>

      {/*==============================
        TYPE OF WORK EXPERIENCE
      ==============================*/}
      <Typography variant="h6" gutterBottom sx={{ p: 2, my: 1, border: "1px solid", borderRadius: 5 }}>
        Type of work experience:
        <Typography variant="body1">
          * Provision of general support functions such as operations, HR, corporate services and IT will not be
          considered as relevant experience.
        </Typography>
        <GridCheckbox
          id="financeGeneric"
          columnSize="12"
          label="Development, Structuring, Management, Sales, Trading, Research, Analysis of Investment Products"
          value="FINANCE_GENERIC"
          disabled={isWorkExperienced === "YES" ? false : true}
          onChange={handleChange}
        />
        <GridCheckbox
          id="financeTrainer"
          columnSize="12"
          label="Provision of training in investment products"
          value="FINANCE_TRAINER"
          disabled={isWorkExperienced === "YES" ? false : true}
          onChange={handleChange}
        />
        <GridCheckbox
          id="listedInvestmentProducts"
          columnSize="12"
          label="Accountancy, Acturial Science, Treasury, Financial Risk Management & Legal Work in financial areas"
          value="FINANCE_LEGAL"
          disabled={isWorkExperienced === "YES" ? false : true}
          onChange={handleChange}
        />
        {experienceTypeError && <FormHelperText error={experienceTypeError}>{experienceTypeError}</FormHelperText>}
      </Typography>

      {/*==============================
        WORK ORGANISATION DETAILS
      ==============================*/}
      {isWorkExperienced === "Yes" && (
        <Grid size={12}>
          <Typography variant="body1">
            Please specify fullname of the business organisation(s) where the above work experience was obtained.
            transactions were carried out.
          </Typography>

          <TextareaAutosize
            id="workOrganisationName"
            placeholder="Type here..."
            style={{ width: "100%" }}
            minRows={5}
            onChange={handleChange}
            defaultValue={workOrganisationName}
          />
          {workOrganisationNameError && (
            <FormHelperText error={workOrganisationNameError}>{workOrganisationNameError}</FormHelperText>
          )}
        </Grid>
      )}
    </>
  );
}
